import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, RefreshCw, School, Printer, Download } from "lucide-react";
import { apiFetch, downloadFile } from "@/api";
import { Button, Field, Spinner, cn } from "@/ui";
import { QRImage } from "@/components/QRImage";
import { fmtDate } from "@/format";
import { LEARNING_LEVELS } from "@/constants";
import QRCode from "qrcode";

const card = "rounded-xl border border-border p-5";
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDay = (s) => (s ? fmtDate(s) : "—");
const STATUSES = ["all", "active", "free trial", "cancelled", "suspended"];
const EDIT_STATUSES = ["active", "suspended", "cancelled"];

function AccessBadge({ access }) {
  if (!access) return null;
  const ok = access.ok;
  const label = !ok
    ? (access.reason === "suspended" ? "Suspended" : "Cancelled")
    : access.via === "trial" ? "Free trial" : "Active";
  return (
    <span data-testid="school-access-badge" className={cn(
      "rounded-full px-2.5 py-0.5 text-xs font-semibold",
      ok ? (access.via === "trial" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800") : "bg-rose-100 text-rose-800")}>
      {label}{ok && access.via === "trial" && access.trial_ends_at ? ` · ends ${fmtDay(access.trial_ends_at)}` : ""}
    </span>
  );
}

function SeatBar({ seats }) {
  if (!seats) return null;
  const pctUsed = seats.total > 0 ? Math.min(100, Math.round((seats.used / seats.total) * 100)) : 0;
  return (
    <div className="flex flex-col gap-1" data-testid="school-seat-summary">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Seats</span>
        <span className="font-semibold">{seats.used} used · {seats.available} free · {seats.total} total</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pctUsed}%` }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- List
function SchoolList({ onOpen, onCreate, onPricing }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (status !== "all") p.set("status", status);
      const d = await apiFetch(`/developer/schools?${p.toString()}`);
      setRows(d.schools || []);
    } catch (e) { toast(e.message); } finally { setLoading(false); }
  }, [q, status]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button key={s} data-testid={`school-status-${s}`} onClick={() => setStatus(s)}
              className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold capitalize",
                status === s ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground")}>{s}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" className="py-2 text-sm" onClick={onPricing} data-testid="school-pricing-btn">Pricing</Button>
          <Button className="py-2 text-sm" onClick={onCreate} data-testid="school-create-btn"><Plus className="mr-1 h-4 w-4" />New school</Button>
        </div>
      </div>
      <div className="flex gap-2">
        <input data-testid="school-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search name, School ID, contact" className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
        <Button variant="secondary" className="py-2 text-sm" onClick={load}>Search</Button>
      </div>
      {loading ? <div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-foreground" /></div> : (
        <>
          {rows.map((s) => (
            <button key={s.id} data-testid={`school-row-${s.id}`} onClick={() => onOpen(s.id)} className={cn(card, "flex items-center justify-between text-left")}>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{s.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{s.school_code}</span>
                  <AccessBadge access={s.access} />
                  {s.pending_requests ? <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-500" data-testid={`school-pending-${s.id}`}>{s.pending_requests} request{s.pending_requests === 1 ? "" : "s"}</span> : null}
                </div>
                <div className="text-sm text-muted-foreground">
                  {s.primary_contact_name} · {s.seats.used}/{s.seats.total} seats · {s.teachers} teacher{s.teachers === 1 ? "" : "s"}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </button>
          ))}
          {rows.length === 0 ? (
            <div className={cn(card, "flex flex-col items-center gap-2 py-10 text-center")}>
              <School className="h-8 w-8 text-muted-foreground" />
              <p className="text-muted-foreground">No schools yet.</p>
              <Button className="py-2 text-sm" onClick={onCreate}><Plus className="mr-1 h-4 w-4" />Create the first school</Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Create
const EMPTY_CREATE = {
  name: "", primary_contact_name: "", contact_title: "", contact_phone: "",
  contact_email: "", admin_login_email: "", admin_password: "", address: "",
  base_licenses: "1", addon_seats: "0", license_expiry: "",
};

function CreateSchool({ onBack, onCreated }) {
  const [f, setF] = useState(EMPTY_CREATE);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name.trim() || !f.primary_contact_name.trim() || !f.contact_phone.trim()
      || !f.contact_email.trim() || !f.admin_login_email.trim() || !f.admin_password.trim()) {
      return toast("Fill in name, contact, phone, emails and admin password.");
    }
    setSaving(true);
    try {
      const body = {
        name: f.name.trim(), primary_contact_name: f.primary_contact_name.trim(),
        contact_title: f.contact_title.trim() || undefined, contact_phone: f.contact_phone.trim(),
        contact_email: f.contact_email.trim(), admin_login_email: f.admin_login_email.trim(),
        admin_password: f.admin_password, address: f.address.trim() || undefined,
        base_licenses: parseInt(f.base_licenses || "0", 10), addon_seats: parseInt(f.addon_seats || "0", 10),
        license_expiry: f.license_expiry || undefined,
      };
      const d = await apiFetch("/developer/schools", { method: "POST", body });
      toast(`School created · ${d.school_code} · 30-day free trial started`);
      onCreated(d.id);
    } catch (e) { toast(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-4">
      <button className="flex items-center gap-1 text-sm" onClick={onBack}><ChevronLeft className="h-4 w-4" />Back</button>
      <h3 className="text-lg font-bold">New school</h3>
      <p className="text-sm text-muted-foreground">Creating a school starts a one-time 30-day free trial automatically. Add or edit paid licenses anytime.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="School name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="school-f-name" />
        <Field label="Primary contact name" value={f.primary_contact_name} onChange={(e) => set("primary_contact_name", e.target.value)} data-testid="school-f-contact" />
        <Field label="Contact title (optional)" value={f.contact_title} onChange={(e) => set("contact_title", e.target.value)} data-testid="school-f-title" />
        <Field label="Contact phone" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="school-f-phone" />
        <Field label="Contact email" type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="school-f-email" />
        <Field label="School admin login email" type="email" value={f.admin_login_email} onChange={(e) => set("admin_login_email", e.target.value)} data-testid="school-f-admin-email" />
        <Field label="School admin password" type="password" value={f.admin_password} onChange={(e) => set("admin_password", e.target.value)} data-testid="school-f-admin-pw" />
        <Field label="Address (optional)" value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="school-f-address" />
        <Field label="Base licenses" type="number" min="0" value={f.base_licenses} onChange={(e) => set("base_licenses", e.target.value)} data-testid="school-f-licenses" />
        <Field label="Add-on seats" type="number" min="0" value={f.addon_seats} onChange={(e) => set("addon_seats", e.target.value)} data-testid="school-f-addon" />
        <Field label="License expiry (optional)" type="date" value={f.license_expiry} onChange={(e) => set("license_expiry", e.target.value)} data-testid="school-f-expiry" />
      </div>
      <Button onClick={submit} loading={saving} data-testid="school-create-submit">Create school</Button>
    </div>
  );
}

// ---------------------------------------------------------------- Pricing config
function PricingConfig({ onBack }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => { try { setP(await apiFetch("/developer/schools/pricing")); } catch (e) { toast(e.message); } })(); }, []);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        seats_per_license: parseInt(p.seats_per_license, 10),
        base_license_price: parseFloat(p.base_license_price),
        addon_seat_price: parseFloat(p.addon_seat_price),
        trial_days: parseInt(p.trial_days, 10),
      };
      setP(await apiFetch("/developer/schools/pricing", { method: "PUT", body }));
      toast("Default pricing saved.");
    } catch (e) { toast(e.message); } finally { setSaving(false); }
  };
  if (!p) return <div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-foreground" /></div>;
  return (
    <div className="flex flex-col gap-4">
      <button className="flex items-center gap-1 text-sm" onClick={onBack}><ChevronLeft className="h-4 w-4" />Back</button>
      <h3 className="text-lg font-bold">Default licensing pricing</h3>
      <p className="text-sm text-muted-foreground">Global defaults for every new school. Each school can override these individually.</p>
      <div className={cn(card, "grid gap-3 sm:grid-cols-2")}>
        <Field label="Seats per base license" type="number" min="0" value={p.seats_per_license} onChange={(e) => set("seats_per_license", e.target.value)} data-testid="pricing-seats" />
        <Field label="Base license price ($)" type="number" min="0" step="0.01" value={p.base_license_price} onChange={(e) => set("base_license_price", e.target.value)} data-testid="pricing-base" />
        <Field label="Add-on seat price ($)" type="number" min="0" step="0.01" value={p.addon_seat_price} onChange={(e) => set("addon_seat_price", e.target.value)} data-testid="pricing-addon" />
        <Field label="Free trial length (days)" type="number" min="1" value={p.trial_days} onChange={(e) => set("trial_days", e.target.value)} data-testid="pricing-trial" />
      </div>
      <Button onClick={save} loading={saving} data-testid="pricing-save">Save defaults</Button>
    </div>
  );
}

// ---------------------------------------------------------------- Detail
function AddLicense({ schoolId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ base_licenses: "0", addon_seats: "1" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        base_licenses: parseInt(f.base_licenses || "0", 10),
        addon_seats: parseInt(f.addon_seats || "0", 10),
      };
      await apiFetch(`/developer/schools/${schoolId}/licenses`, { method: "POST", body });
      toast("Seats added.");
      setOpen(false); setF({ base_licenses: "0", addon_seats: "1" });
      onAdded();
    } catch (e) { toast(e.message); } finally { setSaving(false); }
  };
  if (!open) return <Button variant="secondary" className="py-2 text-sm" onClick={() => setOpen(true)} data-testid="school-add-license-btn"><Plus className="mr-1 h-4 w-4" />Add seats</Button>;
  return (
    <div className={cn(card, "grid gap-3 sm:grid-cols-2")}>
      <Field label="Individual seats" type="number" min="0" value={f.addon_seats} onChange={(e) => set("addon_seats", e.target.value)} data-testid="lic-addon" />
      <Field label="Full licenses (optional)" type="number" min="0" value={f.base_licenses} onChange={(e) => set("base_licenses", e.target.value)} data-testid="lic-base" />
      <div className="flex gap-2 sm:col-span-2">
        <Button className="py-2 text-sm" onClick={submit} loading={saving} data-testid="lic-save">Add</Button>
        <Button variant="secondary" className="py-2 text-sm" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function SchoolAffiliateCard({ schoolId, linked, prefill, onChanged }) {
  const [mode, setMode] = useState("new");
  const [ct, setCt] = useState(linked?.commission_type || "perpetual");
  const [f, setF] = useState({ affiliate_id: "", coupon_code: "", email: prefill?.contact_email || "", password: "", name: prefill?.name || "" });
  const [busy, setBusy] = useState(false);

  const link = async () => {
    const body = { commission_type: ct };
    if (mode === "existing") {
      if (!f.affiliate_id.trim()) return toast("Enter the affiliate ID to link.");
      body.affiliate_id = f.affiliate_id.trim();
    } else {
      if (!f.coupon_code.trim() || !f.email.trim() || !f.password) return toast("Coupon code, email and password are required.");
      Object.assign(body, { coupon_code: f.coupon_code.toUpperCase(), email: f.email.trim(), password: f.password, name: f.name.trim() });
    }
    setBusy(true);
    try { await apiFetch(`/developer/schools/${schoolId}/affiliate`, { method: "POST", body }); toast("School is now an affiliate."); onChanged && onChanged(); }
    catch (e) { toast(e.message); } finally { setBusy(false); }
  };
  const changeType = async (next) => {
    setCt(next);
    try { await apiFetch(`/developer/schools/${schoolId}/affiliate`, { method: "POST", body: { affiliate_id: linked.id, commission_type: next } }); toast("Commission type updated."); onChanged && onChanged(); }
    catch (e) { toast(e.message); }
  };
  const unlink = async () => {
    if (!window.confirm("Turn off this school acting as an affiliate? Existing referrals and commissions are preserved.")) return;
    try { await apiFetch(`/developer/schools/${schoolId}/affiliate`, { method: "DELETE" }); toast("Affiliate turned off."); onChanged && onChanged(); }
    catch (e) { toast(e.message); }
  };

  return (
    <div className={cn(card, "flex flex-col gap-3")} data-testid="school-affiliate-card">
      <div className="flex items-center justify-between">
        <h4 className="font-bold">School as affiliate</h4>
        {linked
          ? <span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground" data-testid="school-affiliate-badge">Enabled</span>
          : <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-muted-foreground" data-testid="school-affiliate-badge">Off</span>}
      </div>
      {linked ? (
        <div className="flex flex-col gap-2 text-sm">
          <p className="text-muted-foreground">This school earns commission on sign-ups that use its coupon code.</p>
          <div><span className="text-muted-foreground">Coupon code: </span><span data-testid="school-affiliate-coupon" className="font-mono text-base font-bold tracking-wider">{linked.coupon_code || "—"}</span></div>
          <div><span className="text-muted-foreground">Affiliate: </span>{linked.name} · {linked.email}</div>
          <label className="flex max-w-xs flex-col gap-1.5">
            <span className="text-xs font-semibold text-muted-foreground">Commission type</span>
            <select data-testid="school-affiliate-type" value={ct} onChange={(e) => changeType(e.target.value)} className="w-full rounded-xl border border-input bg-background px-4 py-3 text-[15px] outline-none">
              <option value="perpetual">Perpetual</option>
              <option value="six_month">6-month (standard)</option>
            </select>
          </label>
          <Button variant="secondary" className="w-fit py-2 text-sm" onClick={unlink} data-testid="school-affiliate-unlink">Turn off affiliate</Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">Let this school act as an affiliate and earn commission on referrals that use its coupon code.</p>
          <div className="flex gap-2">
            {[["new", "Create new affiliate"], ["existing", "Link existing"]].map(([k, l]) => (
              <button key={k} data-testid={`school-affiliate-mode-${k}`} onClick={() => setMode(k)}
                className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold", mode === k ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground")}>{l}</button>
            ))}
          </div>
          {mode === "new" ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Coupon code" value={f.coupon_code} onChange={(e) => setF({ ...f, coupon_code: e.target.value.toUpperCase() })} placeholder="e.g. SPRINGFIELD" data-testid="school-affiliate-coupon-input" />
              <Field label="Affiliate name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="school-affiliate-name" />
              <Field label="Login email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="school-affiliate-email" />
              <Field label="Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="school-affiliate-password" />
            </div>
          ) : (
            <Field label="Existing affiliate ID" value={f.affiliate_id} onChange={(e) => setF({ ...f, affiliate_id: e.target.value })} placeholder="affiliate account id" data-testid="school-affiliate-id" />
          )}
          <label className="flex max-w-xs flex-col gap-1.5">
            <span className="text-xs font-semibold text-muted-foreground">Commission type</span>
            <select data-testid="school-affiliate-type" value={ct} onChange={(e) => setCt(e.target.value)} className="w-full rounded-xl border border-input bg-background px-4 py-3 text-[15px] outline-none">
              <option value="perpetual">Perpetual (recommended for schools)</option>
              <option value="six_month">6-month (standard)</option>
            </select>
          </label>
          <Button className="w-fit py-2.5 text-sm" onClick={link} disabled={busy} data-testid="school-affiliate-link">{busy ? "Linking…" : "Make school an affiliate"}</Button>
        </div>
      )}
    </div>
  );
}

function SchoolRequestsAdmin({ schoolId }) {
  const [reqs, setReqs] = useState(null);
  const load = useCallback(async () => {
    try { const d = await apiFetch(`/developer/schools/${schoolId}/requests`); setReqs(d.requests || []); } catch (e) { toast(e.message); }
  }, [schoolId]);
  useEffect(() => { load(); }, [load]);
  if (!reqs || reqs.length === 0) return null;
  return (
    <div className={cn(card, "flex flex-col gap-3")} data-testid="school-requests-card">
      <h4 className="font-bold">Seat cancellations</h4>
      <p className="text-sm text-muted-foreground">Schools cancel seats themselves (no approval needed). "Pending" takes effect at the end of their paid month, then shows "Approved".</p>
      <div className="flex flex-col gap-2 text-sm">
        {reqs.map((r) => (
          <div key={r.id} data-testid={`sr-${r.id}`} className="flex items-center justify-between gap-2 border-t border-border pt-2 first:border-t-0 first:pt-0 text-muted-foreground">
            <span>Cancelled {r.seats} seat{r.seats === 1 ? "" : "s"} · {r.requested_by_name}{r.effective_date ? ` · effective ${fmtDay(r.effective_date)}` : ""}</span>
            <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize", r.status === "approved" ? "bg-primary text-primary-foreground" : "bg-amber-500/15 text-amber-500")} data-testid={`sr-status-${r.id}`}>{r.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SchoolDetail({ schoolId, onBack, onChanged }) {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [ef, setEf] = useState({});

  const load = useCallback(async () => {
    try { const data = await apiFetch(`/developer/schools/${schoolId}`); setD(data); setEf(data); } catch (e) { toast(e.message); }
  }, [schoolId]);
  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    try {
      const body = {
        name: ef.name, primary_contact_name: ef.primary_contact_name, contact_title: ef.contact_title,
        contact_phone: ef.contact_phone, contact_email: ef.contact_email, address: ef.address, status: ef.status,
      };
      await apiFetch(`/developer/schools/${schoolId}`, { method: "PUT", body });
      toast("Saved."); setEditing(false); load(); onChanged && onChanged();
    } catch (e) { toast(e.message); }
  };
  const resetTrial = async () => {
    const days = window.prompt("Extend the free trial by how many days?", "30");
    if (!days) return;
    const reason = window.prompt("Reason (optional):", "") || undefined;
    try { await apiFetch(`/developer/schools/${schoolId}/trial/reset`, { method: "POST", body: { days: parseInt(days, 10), reason } }); toast("Trial extended."); load(); onChanged && onChanged(); } catch (e) { toast(e.message); }
  };
  const voidLicense = async (lid) => {
    if (!window.confirm("Void this license block? Its seats are removed from capacity.")) return;
    try { await apiFetch(`/developer/schools/${schoolId}/licenses/${lid}`, { method: "DELETE" }); toast("License voided."); load(); onChanged && onChanged(); } catch (e) { toast(e.message); }
  };

  if (!d) return <div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-foreground" /></div>;

  return (
    <div className="flex flex-col gap-4">
      <button className="flex items-center gap-1 text-sm" onClick={onBack}><ChevronLeft className="h-4 w-4" />Back to schools</button>

      <div className={cn(card, "flex flex-col gap-3")}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold">{d.name}</span>
            <span className="font-mono text-xs text-muted-foreground">{d.school_code}</span>
            <AccessBadge access={d.access} />
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" className="py-1.5 text-xs" onClick={resetTrial} data-testid="school-reset-trial"><RefreshCw className="mr-1 h-3.5 w-3.5" />Extend trial</Button>
            <Button variant="secondary" className="py-1.5 text-xs" onClick={() => setEditing((v) => !v)} data-testid="school-edit-btn">{editing ? "Close" : "Edit"}</Button>
          </div>
        </div>
        <SeatBar seats={d.seats} />
        {!editing ? (
          <div className="grid gap-1 text-sm sm:grid-cols-2">
            <div><span className="text-muted-foreground">Contact: </span>{d.primary_contact_name}{d.contact_title ? ` (${d.contact_title})` : ""}</div>
            <div><span className="text-muted-foreground">Status: </span><span className="capitalize">{d.status}</span></div>
            <div><span className="text-muted-foreground">Phone: </span>{d.contact_phone}</div>
            <div><span className="text-muted-foreground">Email: </span>{d.contact_email}</div>
            <div><span className="text-muted-foreground">Admin login: </span>{d.admin_login_email}</div>
            <div><span className="text-muted-foreground">Trial ends: </span>{fmtDay(d.trial_ends_at)}{d.trial_reset_count ? ` · extended ${d.trial_reset_count}×` : ""}</div>
            {d.address ? <div className="sm:col-span-2"><span className="text-muted-foreground">Address: </span>{d.address}</div> : null}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="School name" value={ef.name || ""} onChange={(e) => setEf({ ...ef, name: e.target.value })} data-testid="edit-name" />
            <Field label="Primary contact" value={ef.primary_contact_name || ""} onChange={(e) => setEf({ ...ef, primary_contact_name: e.target.value })} data-testid="edit-contact" />
            <Field label="Contact title" value={ef.contact_title || ""} onChange={(e) => setEf({ ...ef, contact_title: e.target.value })} />
            <Field label="Phone" value={ef.contact_phone || ""} onChange={(e) => setEf({ ...ef, contact_phone: e.target.value })} />
            <Field label="Contact email" value={ef.contact_email || ""} onChange={(e) => setEf({ ...ef, contact_email: e.target.value })} />
            <Field label="Address" value={ef.address || ""} onChange={(e) => setEf({ ...ef, address: e.target.value })} />
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Status</span>
              <select data-testid="edit-status" value={ef.status || "active"} onChange={(e) => setEf({ ...ef, status: e.target.value })}
                className="w-full rounded-xl border border-input bg-background px-4 py-3 text-[15px] outline-none">
                {EDIT_STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </label>
            <div className="flex items-end"><Button className="py-2.5 text-sm" onClick={saveEdit} data-testid="edit-save">Save changes</Button></div>
          </div>
        )}
      </div>

      <div className={cn(card, "flex flex-col gap-3")}>
        <div className="flex items-center justify-between">
          <h4 className="font-bold">Licenses</h4>
          <span className="text-sm text-muted-foreground">Pricing: {d.pricing.seats_per_license} seats / {money(d.pricing.base_license_price)} · add-on {money(d.pricing.addon_seat_price)}/seat</span>
        </div>
        {d.licenses.length === 0 ? <p className="text-sm text-muted-foreground">No paid licenses yet — the school is on its free trial.</p> : (
          <div className="flex flex-col gap-2">
            {d.licenses.map((l) => (
              <div key={l.id} data-testid={`license-${l.id}`} className={cn("flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm", l.status === "void" ? "opacity-50" : "")}>
                <div>
                  {l.kind === "reduction" ? (
                    <>
                      <div className="font-semibold text-rose-500">Seat cancellation · {Math.abs(l.seats)} seat{Math.abs(l.seats) === 1 ? "" : "s"} removed</div>
                      <div className="text-muted-foreground">approved{l.created_at ? ` ${fmtDay(l.created_at)}` : ""}</div>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold">{l.quantity}× base ({l.seats_per_license} ea){l.addon_seats ? ` + ${l.addon_seats} add-on` : ""} = {l.seats} seats{l.source === "self_purchase" ? " · self-purchased" : ""}</div>
                      <div className="text-muted-foreground">{money(l.total_price)} · {l.status}{l.expiry_date ? ` · expires ${fmtDay(l.expiry_date)}` : ""}</div>
                    </>
                  )}
                </div>
                {l.status !== "void" && l.kind !== "reduction" ? (
                  <button data-testid={`license-void-${l.id}`} onClick={() => voidLicense(l.id)} className="text-rose-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                ) : null}
              </div>
            ))}
          </div>
        )}
        <AddLicense schoolId={schoolId} onAdded={() => { load(); onChanged && onChanged(); }} />
      </div>

      <SchoolRequestsAdmin schoolId={schoolId} onChanged={() => { load(); onChanged && onChanged(); }} />

      <SchoolAffiliateCard schoolId={schoolId} linked={d.linked_affiliate} prefill={d} onChanged={() => { load(); onChanged && onChanged(); }} />
    </div>
  );
}

// ---------------------------------------------------------------- School Admin portal panel
function StatCard({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className={cn("flex flex-col gap-1 rounded-xl border border-border p-4", accent ? "bg-primary/5" : "")}>
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-2xl font-bold">{value}</span>
    </div>
  );
}

function AddTeacher({ seatsLeft, onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", seat_allocation: "0" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.password.trim()) return toast("Name, email and password are required.");
    setSaving(true);
    try {
      await apiFetch("/school/teachers", { method: "POST", body: {
        name: f.name.trim(), email: f.email.trim(), password: f.password,
        seat_allocation: parseInt(f.seat_allocation || "0", 10),
      } });
      toast("Teacher added.");
      setOpen(false); setF({ name: "", email: "", password: "", seat_allocation: "0" });
      onAdded();
    } catch (e) { toast(e.message); } finally { setSaving(false); }
  };
  if (!open) return <Button variant="secondary" className="py-2 text-sm" onClick={() => setOpen(true)} data-testid="sa-add-teacher-btn"><Plus className="mr-1 h-4 w-4" />Add teacher</Button>;
  return (
    <div className={cn(card, "grid gap-3 sm:grid-cols-2")}>
      <Field label="Teacher name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="sa-t-name" />
      <Field label="Login email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="sa-t-email" />
      <Field label="Password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="sa-t-password" />
      <Field label={`Seats (max ${seatsLeft} left)`} type="number" min="0" value={f.seat_allocation} onChange={(e) => set("seat_allocation", e.target.value)} data-testid="sa-t-seats" />
      <div className="flex gap-2 sm:col-span-2">
        <Button className="py-2 text-sm" onClick={submit} loading={saving} data-testid="sa-t-save">Add teacher</Button>
        <Button variant="secondary" className="py-2 text-sm" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function TeacherRow({ t, seatsAvailableToGrant, onChanged }) {
  const [alloc, setAlloc] = useState(String(t.seat_allocation));
  const [editing, setEditing] = useState(false);
  const saveAlloc = async () => {
    try { await apiFetch(`/school/teachers/${t.id}`, { method: "PUT", body: { seat_allocation: parseInt(alloc || "0", 10) } }); toast("Seats updated."); setEditing(false); onChanged(); } catch (e) { toast(e.message); }
  };
  const resetPw = async () => {
    const pw = window.prompt(`Set a new password for ${t.name}:`, "");
    if (!pw) return;
    try { await apiFetch(`/school/teachers/${t.id}`, { method: "PUT", body: { password: pw } }); toast("Password reset."); } catch (e) { toast(e.message); }
  };
  const toggleStatus = async () => {
    const next = t.status === "active" ? "inactive" : "active";
    try { await apiFetch(`/school/teachers/${t.id}`, { method: "PUT", body: { status: next } }); toast(`Teacher ${next}.`); onChanged(); } catch (e) { toast(e.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${t.name}? This is blocked if they still have enrolled students.`)) return;
    try { await apiFetch(`/school/teachers/${t.id}`, { method: "DELETE" }); toast("Teacher removed."); onChanged(); } catch (e) { toast(e.message); }
  };
  return (
    <div data-testid={`sa-teacher-${t.id}`} className={cn("flex flex-col gap-2 rounded-lg border border-border p-3", t.status !== "active" ? "opacity-60" : "")}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold">{t.name}{t.status !== "active" ? " · inactive" : ""}</div>
          <div className="text-sm text-muted-foreground">{t.email}</div>
        </div>
        <div className="text-right text-sm">
          <div className="font-semibold">{t.used}/{t.seat_allocation} seats</div>
          <div className="text-muted-foreground">{t.available} free</div>
        </div>
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <input data-testid={`sa-alloc-input-${t.id}`} type="number" min={t.used} value={alloc} onChange={(e) => setAlloc(e.target.value)} className="w-24 rounded-lg border border-input bg-background px-3 py-1.5 text-sm outline-none" />
          <Button className="py-1.5 text-xs" onClick={saveAlloc} data-testid={`sa-alloc-save-${t.id}`}>Save</Button>
          <Button variant="secondary" className="py-1.5 text-xs" onClick={() => { setEditing(false); setAlloc(String(t.seat_allocation)); }}>Cancel</Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button data-testid={`sa-alloc-edit-${t.id}`} onClick={() => setEditing(true)} className="rounded-full border border-border px-3 py-1 text-xs font-semibold">Edit seats</button>
          <button data-testid={`sa-t-reset-${t.id}`} onClick={resetPw} className="rounded-full border border-border px-3 py-1 text-xs font-semibold">Reset password</button>
          <button data-testid={`sa-t-toggle-${t.id}`} onClick={toggleStatus} className="rounded-full border border-border px-3 py-1 text-xs font-semibold">{t.status === "active" ? "Deactivate" : "Activate"}</button>
          <button data-testid={`sa-t-remove-${t.id}`} onClick={remove} className="rounded-full border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-500">Remove</button>
        </div>
      )}
    </div>
  );
}

function BuySeats({ pricing, onPaid }) {
  const [packs, setPacks] = useState(0);
  const [seats, setSeats] = useState(0);
  const [busy, setBusy] = useState(false);
  const spl = pricing?.seats_per_license || 30;
  const base = pricing?.base_license_price || 0;
  const addon = pricing?.addon_seat_price || 0;
  const totalSeats = packs * spl + seats;
  const amount = packs * base + seats * addon;
  const pay = async () => {
    if (totalSeats <= 0) return toast("Add at least one pack or seat.");
    setBusy(true);
    try {
      const r = await apiFetch("/school/seats/checkout", { method: "POST", body: { packs, seats, origin_url: window.location.origin } });
      if (r.updated) { toast(`${r.added_seats} seat${r.added_seats === 1 ? "" : "s"} added to your monthly plan.`); setPacks(0); setSeats(0); setBusy(false); onPaid && onPaid(); return; }
      window.location.href = r.checkout_url;
    } catch (e) { toast(e.message); setBusy(false); }
  };
  const num = (v) => Math.max(0, parseInt(v || "0", 10) || 0);
  return (
    <div className={cn(card, "flex flex-col gap-3")} data-testid="buy-seats-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-bold">Buy more seats</h4>
        <span className="text-sm text-muted-foreground">{money(base)}/mo per {spl}-seat pack · {money(addon)}/mo per seat</span>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">{spl}-seat packs
          <input data-testid="buy-packs" type="number" min="0" value={packs} onChange={(e) => setPacks(num(e.target.value))} className="mt-1 w-24 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">Individual seats
          <input data-testid="buy-seats-input" type="number" min="0" value={seats} onChange={(e) => setSeats(num(e.target.value))} className="mt-1 w-24 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
        </label>
        <div className="text-sm"><div className="text-xs text-muted-foreground">Adds</div><div className="font-bold" data-testid="buy-total-seats">{totalSeats} seats</div></div>
        <div className="text-sm"><div className="text-xs text-muted-foreground">Monthly</div><div className="font-bold" data-testid="buy-total-amount">{money(amount)}/mo</div></div>
      </div>
      <Button className="w-fit py-2.5 text-sm" onClick={pay} disabled={busy || totalSeats <= 0} data-testid="buy-pay-btn">{busy ? "Redirecting…" : "Pay with Stripe"}</Button>
      <p className="text-[11px] text-muted-foreground">Billed monthly. New seats are added instantly — if you already have a plan they're added to it (prorated). Reduce or cancel anytime; changes take effect at the end of your paid month.</p>
    </div>
  );
}

function SeatRequests({ requests, seats, onChanged }) {
  const [n, setN] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const inUse = seats?.used || 0;
  const canCancel = Math.max(0, (seats?.total || 0) - inUse);
  const submit = async () => {
    const v = parseInt(n || "0", 10);
    if (!v || v <= 0) return toast("Enter how many seats to cancel.");
    if (v > canCancel) return toast(`You can cancel up to ${canCancel} seat(s) right now (${inUse} in use).`);
    if (!window.confirm(`Cancel ${v} seat${v === 1 ? "" : "s"}? They stay available until the end of your current paid month, then your monthly bill drops.`)) return;
    setBusy(true);
    try {
      const r = await apiFetch("/school/seats/reduce", { method: "POST", body: { seats: v, note: note.trim() || undefined } });
      toast(r.effective_date ? `Cancellation scheduled — effective ${fmtDay(r.effective_date)}.` : "Seats cancelled.");
      setN(""); setNote(""); onChanged && onChanged();
    } catch (e) { toast(e.message); } finally { setBusy(false); }
  };
  return (
    <div className={cn(card, "flex flex-col gap-3")} data-testid="reduce-seats-card">
      <h4 className="font-bold">Cancel / reduce seats</h4>
      <p className="text-sm text-muted-foreground">Cancel seats you no longer need — no approval required. They stay available until the end of your current paid month, then your capacity and monthly bill drop. You can cancel up to <span className="font-semibold text-foreground">{canCancel}</span> right now ({inUse} in use).</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">Seats to cancel
          <input data-testid="reduce-seats-input" type="number" min="1" max={canCancel} value={n} onChange={(e) => setN(e.target.value)} className="mt-1 w-28 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
        </label>
        <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-xs text-muted-foreground">Note (optional)
          <input data-testid="reduce-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
        </label>
        <Button variant="secondary" className="py-2 text-sm" onClick={submit} disabled={busy || canCancel <= 0} data-testid="reduce-submit">{busy ? "Cancelling…" : "Cancel seats"}</Button>
      </div>
      {requests?.length ? (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Cancellation history</div>
          {requests.map((r) => (
            <div key={r.id} data-testid={`req-${r.id}`} className="flex items-center justify-between gap-2 text-sm">
              <span>Cancelled {r.seats} seat{r.seats === 1 ? "" : "s"}{r.note ? ` · ${r.note}` : ""}{r.effective_date ? ` · effective ${fmtDay(r.effective_date)}` : ""}</span>
              <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize", r.status === "approved" ? "bg-primary text-primary-foreground" : "bg-amber-500/15 text-amber-500")} data-testid={`req-status-${r.id}`}>{r.status}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BillingHistory() {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      const q = p.toString();
      setData(await apiFetch(`/school/billing${q ? `?${q}` : ""}`));
    } catch (e) { toast(e.message); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);
  const months = data?.available_month_keys || [];
  const thisYear = () => { const y = new Date().getFullYear(); setFrom(`${y}-01`); setTo(`${y}-12`); };
  const allTime = () => { setFrom(""); setTo(""); };
  const dl = () => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const q = p.toString();
    downloadFile(`/school/billing.csv${q ? `?${q}` : ""}`, "seat_billing_history.csv").catch((e) => toast(e.message));
  };
  const rows = data?.rows || [];
  const qLabel = (m) => { const [y, mo] = m.split("-"); return `${mo}/${y}`; };
  return (
    <div className={cn(card, "flex flex-col gap-3")} data-testid="billing-history-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-bold">Billing history</h4>
        <Button variant="secondary" className="py-1.5 text-sm" onClick={dl} data-testid="billing-download"><Download className="h-4 w-4" /> Download CSV</Button>
      </div>
      <p className="text-sm text-muted-foreground">Seats you paid for each month, seats added mid-month (prorated), and seats cancelled that month. Paid months link to the Stripe receipt.</p>
      <div className="flex flex-wrap items-end gap-2">
        <button type="button" onClick={allTime} className={cn("rounded-full border px-3 py-1 text-xs font-semibold", !from && !to ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground")} data-testid="billing-filter-all">All time</button>
        <button type="button" onClick={thisYear} className="rounded-full border border-border px-3 py-1 text-xs font-semibold text-muted-foreground" data-testid="billing-filter-year">This year</button>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">From
          <select data-testid="billing-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-lg border border-input bg-background px-2 py-1.5 text-sm">
            <option value="">—</option>
            {months.map((m) => <option key={m} value={m}>{qLabel(m)}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">To
          <select data-testid="billing-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-lg border border-input bg-background px-2 py-1.5 text-sm">
            <option value="">—</option>
            {months.map((m) => <option key={m} value={m}>{qLabel(m)}</option>)}
          </select>
        </label>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="billing-empty">No billing activity in this range. Buy seats to start a monthly subscription.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-4">Month</th>
                <th className="py-2 pr-4">Seats paid</th>
                <th className="py-2 pr-4">Prorated added</th>
                <th className="py-2 pr-4">Cancelled</th>
                <th className="py-2 pr-4">Monthly charge</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.month} data-testid={`billing-row-${r.month}`} className="border-t border-border">
                  <td className="py-2 pr-4 font-medium">{r.month_label}</td>
                  <td className="py-2 pr-4" data-testid={`billing-paid-${r.month}`}>{r.seats_paid}</td>
                  <td className="py-2 pr-4" data-testid={`billing-prorated-${r.month}`}>{r.prorated_seats_added || "—"}</td>
                  <td className="py-2 pr-4" data-testid={`billing-cancelled-${r.month}`}>{r.seats_cancelled || "—"}</td>
                  <td className="py-2 pr-4">{money(r.monthly_charge)}</td>
                  <td className="py-2 pr-4 capitalize">{r.status}</td>
                  <td className="py-2">{r.invoice_pdf ? <a href={r.invoice_pdf} target="_blank" rel="noreferrer" className="text-primary underline" data-testid={`billing-invoice-${r.month}`}>PDF</a> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SchoolAdminPanel() {
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try { setD(await apiFetch("/school/me")); } catch (e) { toast(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("seats_paid") === "1" && sp.get("session_id")) {
      const sid = sp.get("session_id");
      window.history.replaceState({}, "", "/portal/school");
      (async () => {
        for (let i = 0; i < 8; i++) {
          try { const r = await apiFetch(`/school/seats/status/${sid}`); if (r.payment_status === "paid") { toast("Payment received — seats added!"); load(); return; } } catch { /* retry */ }
          await new Promise((res) => setTimeout(res, 1500));
        }
        toast("Payment is processing — your seats will appear shortly."); load();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!d) return <div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-foreground" /></div>;
  const seatsLeftToAllocate = d.seats.unallocated;
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold">{d.school.name}</span>
          <span className="font-mono text-xs text-muted-foreground">{d.school.school_code}</span>
          <AccessBadge access={d.school.access} />
        </div>
        <span className="text-sm text-muted-foreground">
          {d.school.next_expiry ? `Licence until ${fmtDay(d.school.next_expiry)}` : d.school.access?.via === "trial" ? `Free trial until ${fmtDay(d.school.trial_ends_at)}` : ""}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label="Total seats" value={d.seats.total} testid="sa-seats-total" />
        <StatCard label="In use" value={d.seats.used} testid="sa-seats-used" />
        <StatCard label="Available" value={d.seats.available} testid="sa-seats-available" accent />
        <StatCard label="Unallocated" value={d.seats.unallocated} testid="sa-seats-unallocated" />
      </div>

      <div className={cn(card, "flex flex-col gap-3")}>
        <div className="flex items-center justify-between">
          <h4 className="font-bold">Teachers <span className="text-muted-foreground">({d.teachers.length})</span></h4>
          <span className="text-sm text-muted-foreground">{seatsLeftToAllocate} seat(s) left to allocate</span>
        </div>
        {d.teachers.length === 0 ? <p className="text-sm text-muted-foreground">No teachers yet. Add one and allocate seats from your pool.</p> : (
          <div className="flex flex-col gap-2">
            {d.teachers.map((t) => <TeacherRow key={t.id} t={t} seatsAvailableToGrant={seatsLeftToAllocate} onChanged={load} />)}
          </div>
        )}
        <AddTeacher seatsLeft={seatsLeftToAllocate} onAdded={load} />
      </div>

      <BuySeats pricing={d.pricing} onPaid={load} />
      <SeatRequests requests={d.requests} seats={d.seats} onChanged={load} />
      <BillingHistory />
    </div>
  );
}

// ---------------------------------------------------------------- Teacher portal panel
export function TeacherPanel() {
  const [d, setD] = useState(null);
  const [starting, setStarting] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [minutes, setMinutes] = useState(60);
  const load = useCallback(async () => {
    try { setD(await apiFetch("/teacher/me")); } catch (e) { toast(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Live refresh so the roster ticks up + seats/countdown stay in sync as kids join.
  useEffect(() => {
    const iv = setInterval(() => { load(); }, 5000);
    return () => clearInterval(iv);
  }, [load]);

  const expiresAt = d?.session?.expires_at;
  useEffect(() => {
    if (!expiresAt) { setRemaining(0); return; }
    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setRemaining(secs);
      if (secs <= 0) load();
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [expiresAt, load]);

  if (!d) return <div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-foreground" /></div>;
  const copy = (t) => { try { navigator.clipboard.writeText(t); toast("Copied."); } catch { toast("Copy failed."); } };
  const saveLevel = async (lvl) => { try { await apiFetch("/teacher/class", { method: "PUT", body: { learning_level: lvl } }); toast("Class learning level updated."); load(); } catch (e) { toast(e.message); } };
  const startSession = async () => { setStarting(true); try { await apiFetch("/teacher/session-code", { method: "POST", body: { minutes } }); toast("Session started."); await load(); } catch (e) { toast(e.message); } finally { setStarting(false); } };
  const endSession = async () => { if (!window.confirm("End this session? Everyone signed in with this code is logged out.")) return; try { await apiFetch("/teacher/session-code", { method: "DELETE" }); toast("Session ended."); await load(); } catch (e) { toast(e.message); } };
  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const ago = (iso) => { if (!iso) return ""; const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000)); if (s < 60) return "just now"; const m = Math.floor(s / 60); return m < 60 ? `${m}m ago` : `${Math.floor(m / 60)}h ago`; };
  const session = d.session;
  const roster = d.students || [];
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold">{d.school.name}</span>
          <span className="font-mono text-xs text-muted-foreground">{d.school.school_code}</span>
          <AccessBadge access={d.school.access} />
        </div>
        <span className="text-sm text-muted-foreground" data-testid="tp-seats">{d.seats.used}/{d.seats.allocation} seats in use · {d.seats.available} free</span>
      </div>

      <div className={cn(card, "flex flex-col items-center gap-4 text-center")}>
        <h4 className="self-start font-bold">Class session code</h4>
        <p className="self-start text-sm text-muted-foreground">
          Generate a code and share it with your class (write it on the board). Students tap <span className="font-semibold">"Session Code?"</span> on the sign-in screen — web or mobile — then type the code and their name to get straight into MAXx. When the time's up everyone is signed out. Session logins don't save chat history or XP.
        </p>
        {session ? (
          <>
            <div data-testid="tp-session-code" className="w-full rounded-2xl border border-primary/40 bg-primary/10 py-6">
              <div className="font-mono text-5xl font-bold tracking-[0.3em]">{session.code}</div>
            </div>
            <div data-testid="tp-session-timer" className="text-sm font-semibold text-muted-foreground">Expires in {mmss(remaining)}</div>
            <div className="flex w-full flex-wrap gap-2">
              <Button variant="secondary" className="flex-1 py-2 text-sm" onClick={() => copy(session.code)} data-testid="tp-copy-code">Copy code</Button>
              <Button variant="secondary" className="flex-1 py-2 text-sm" onClick={startSession} loading={starting} data-testid="tp-new-code">New code</Button>
              <Button variant="secondary" className="flex-1 py-2 text-sm text-rose-500" onClick={endSession} data-testid="tp-end-session">End session</Button>
            </div>
          </>
        ) : (
          <div className="flex w-full flex-col gap-3">
            <div className="flex flex-col gap-1.5 text-left">
              <span className="text-xs font-semibold text-muted-foreground">Session length</span>
              <div className="flex gap-2">
                {[30, 60, 90].map((m) => (
                  <button key={m} data-testid={`tp-minutes-${m}`} onClick={() => setMinutes(m)} className={cn("flex-1 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors", minutes === m ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground")}>{m} min</button>
                ))}
              </div>
            </div>
            <Button className="w-full" onClick={startSession} loading={starting} data-testid="tp-start-session">Start a {minutes}-minute session</Button>
          </div>
        )}
        <div className="flex w-full flex-col gap-1 text-left">
          <label className="text-xs font-semibold text-muted-foreground">Class learning level — students who join start at this level</label>
          <select data-testid="tp-class-level" value={d.teacher.class_level || "5th"} onChange={(e) => saveLevel(e.target.value)} className="rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none">
            {LEARNING_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>

      {session ? (
        <div className={cn(card, "flex flex-col gap-3")}>
          <h4 className="font-bold">In class now <span className="text-muted-foreground" data-testid="tp-active-count">({d.active_count})</span></h4>
          {roster.length === 0 ? (
            <p data-testid="tp-roster-empty" className="text-sm text-muted-foreground">No one has joined yet. Students who enter the code appear here as they sign in.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {roster.map((st) => (
                <div key={st.id} data-testid={`tp-roster-${st.id}`} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <span className="font-semibold">{st.name}</span>
                  <span className="text-xs text-muted-foreground">joined {ago(st.joined)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Root
export function SchoolCenter() {
  const [view, setView] = useState({ mode: "list" });
  const [nonce, setNonce] = useState(0);
  const key = `${view.mode}-${view.id || ""}-${nonce}`;
  if (view.mode === "create") return <CreateSchool key={key} onBack={() => setView({ mode: "list" })} onCreated={(id) => setView({ mode: "detail", id })} />;
  if (view.mode === "pricing") return <PricingConfig key={key} onBack={() => setView({ mode: "list" })} />;
  if (view.mode === "detail") return <SchoolDetail key={key} schoolId={view.id} onBack={() => { setNonce((n) => n + 1); setView({ mode: "list" }); }} onChanged={() => setNonce((n) => n + 1)} />;
  return <SchoolList key={key} onOpen={(id) => setView({ mode: "detail", id })} onCreate={() => setView({ mode: "create" })} onPricing={() => setView({ mode: "pricing" })} />;
}
